import React, { ComponentProps } from 'react'

import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'

import { InformationOutlineIcon } from '@arcade/web/icons/react'
import { ArcadeAvatar, BaseCell, Flex, RadioCell, Stack, Text } from '@cash-design-system/react'
import {
  InstrumentSelectionBlockerV3,
  InstrumentSelectionBlockerV3IconBackgroundColor,
  InstrumentSelectionBlockerV3InstrumentOption,
  InstrumentSelectionBlockerV3SelectInstrumentAction,
} from '@cash-web/protos/squareup/franklin/blockers/instrument_selection_blocker_v3.pb'
import logger from '@cash-web/shared/util-logger'

export type BlockerInstrumentSelectionV3Props = {
  blocker: InstrumentSelectionBlockerV3
}

type SelectInstrumentActionWithToken = InstrumentSelectionBlockerV3SelectInstrumentAction & {
  instrumentToken: string
}

function hasInstrumentToken(
  action: InstrumentSelectionBlockerV3SelectInstrumentAction | undefined
): action is SelectInstrumentActionWithToken {
  return action !== undefined && action.instrumentToken !== undefined
}

function getIconColors(
  backgroundColor: InstrumentSelectionBlockerV3IconBackgroundColor
): Pick<ComponentProps<typeof ArcadeAvatar>, 'backgroundColor' | 'tintColor'> {
  switch (backgroundColor) {
    case 'BRAND':
      return { backgroundColor: 'backgroundBrand', tintColor: 'iconInverse' }
    case 'NONE':
      return { backgroundColor: 'constantClear', tintColor: 'iconStandard' }
    case 'DEFAULT':
    default:
      if (backgroundColor !== 'DEFAULT') {
        logger.warn(new Error(`InstrumentSelectionBlockerV3 icon background color not implemented: ${backgroundColor}`))
      }
      return { backgroundColor: 'backgroundSubtle', tintColor: 'iconSubtle' }
  }
}

const InfoIconContainer = styled.span`
  height: 1lh;
  display: inline-flex;
  align-items: center;
`

export const InstrumentSelectionV3Option: React.FC<
  InstrumentSelectionBlockerV3InstrumentOption & {
    selectedOptionToken?: string
    onSelect: (optionToken: string) => void
  }
> = ({
  title = '',
  subtitle,
  tertiary_label,
  info_message,
  icon_background_color,
  url,
  arcade_id,
  select_instrument_action,
  link_instrument_action,
  replace_instrument_action,
  show_sheet_instrument_action,
  no_instrument_action,
  selectedOptionToken,
  onSelect,
}) => {
  const { spacing } = useTheme()

  const iconColors = icon_background_color ? getIconColors(icon_background_color) : undefined

  // TODO: Once server stops sending us these actions should we change these to warn?
  const notYetSupportedMsg = (actionName: string) => `BlockerInstrumentSelectionV3: ${actionName} not yet supported`
  if (link_instrument_action) {
    logger.info(new Error(notYetSupportedMsg('link_instrument_action')))
    return null
  } else if (replace_instrument_action) {
    logger.info(new Error(notYetSupportedMsg('replace_instrument_action')))
    return null
  } else if (show_sheet_instrument_action) {
    logger.info(new Error(notYetSupportedMsg('show_sheet_instrument_action')))
    return null
  }

  if (select_instrument_action && !hasInstrumentToken(select_instrument_action)) {
    // If this error is encountered, let the server team know because this should never happen.
    logger.info(
      new Error('InstrumentSelectionV3Option: there is a select_instrument_action without an instrumentToken')
    )
    return null
  }

  const label = title
  const body = tertiary_label?.label ? (
    <>
      {subtitle && <Text>{subtitle}</Text>}
      <br />
      <Text color={tertiary_label.color === 'DANGER' ? 'textDanger' : undefined}>{tertiary_label.label}</Text>
    </>
  ) : (
    subtitle
  )
  const cellProps = {
    body,
    asset: {
      avatar: (
        <ArcadeAvatar
          size="small"
          backgroundColor={iconColors?.backgroundColor}
          tintColor={iconColors?.tintColor}
          image={url}
          icon={arcade_id ? { arcadeId: arcade_id } : undefined}
        />
      ),
    },
  }

  const infoMessageElement = info_message ? (
    <Flex flexDirection="row" gap={spacing.xxs}>
      <InfoIconContainer>
        <InformationOutlineIcon size={16} variant="standard" />
      </InfoIconContainer>
      <Text typography="helpText" color="textStandard">
        {info_message}
      </Text>
    </Flex>
  ) : null

  return (
    <Stack key={title} space={spacing.l}>
      {select_instrument_action && (
        <RadioCell
          label={label}
          cellProps={cellProps}
          checked={select_instrument_action.instrumentToken === selectedOptionToken}
          onChange={() => onSelect(select_instrument_action.instrumentToken)}
        />
      )}
      {no_instrument_action && <BaseCell label={label} {...cellProps} />}
      {infoMessageElement}
    </Stack>
  )
}
